import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  IconButton,
  TextField,
  InputAdornment,
  Typography,
  Box,
} from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import useProfile from 'hooks/useProfile';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';
import type { FC } from 'react';
import type { DefaultPopupProps } from 'utils/props';

import Button from 'components/Button';
import ErrorText from 'components/ErrorText';
import Modal from 'components/Modal';
import StatusMessageBox from 'components/StatusMessageBox';

import { useAppDispatch } from 'redux-store';
import type { DeviceSerial } from 'redux-store/slices/ui/types';
import addDeviceThunk from 'redux-store/thunks/devices/addDeviceThunk';
import getAllDevicesThunk from 'redux-store/thunks/devices/getAllDevicesThunk';

export interface GainFullDeviceAccessModalProps extends DefaultPopupProps {
  serial?: DeviceSerial;
}

const GainFullDeviceAccessModal: FC<GainFullDeviceAccessModalProps> = ({
  open,
  onClose,
  serial,
}) => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const profile = useProfile();
  const router = useRouter();

  const [password, setPassword] = useState<string>('');

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);

  const [showPassword, setShowPassword] = useState<boolean>(false);

  const handleAuthenticateDevice = useCallback(
    async (event: { preventDefault: () => void }) => {
      event.preventDefault();
      if (isLoading || !serial || password.length < 6) {
        return;
      }

      try {
        setLoading(true);
        const result = await dispatch(
          addDeviceThunk({ serial: serial, password }),
        ).unwrap();

        if (!result.success) {
          if (i18n.exists(result.error)) {
            setError(t(result.error));
          } else {
            setError(result.error);
          }
        } else {
          if (profile) {
            await dispatch(getAllDevicesThunk({ userId: profile.id }));
          }
          setSuccess(true);
          setPassword('');

          setTimeout(() => {
            onClose();
            setSuccess(false);
            // reload page after successful authentication to trigger connection with wsmux
            router.reload();
          }, 1250);
        }
      } catch (e) {
        Sentry.captureException(e, {
          extra: {
            serial,
          },
          tags: {
            page: 'GainFullDeviceAccessModal',
          },
        });
      } finally {
        setLoading(false);
      }
    },
    [isLoading, serial, password, dispatch, i18n, t, profile, onClose, router],
  );

  return (
    <Modal open={open} onClose={onClose} hideCloseButton={success}>
      {!success ? (
        <Box height="100%" sx={{ overflowY: 'auto' }}>
          <form onSubmit={handleAuthenticateDevice}>
            <Box mb={3}>
              <Typography
                variant="h2"
                textAlign="center"
                data-testid="new-device-title"
                mb={2}
              >
                {t('common:pages.device.authenticate_device.title')}
              </Typography>
              <Typography variant="body1" textAlign="center">
                {t('common:pages.device.authenticate_device.description')}
              </Typography>
            </Box>
            <Box mb={2}>
              <TextField
                label={t('common:serial_number')}
                name="serial"
                value={serial}
                type="text"
                fullWidth
                variant="filled"
                disabled
              />
            </Box>
            <Box mb={4}>
              <TextField
                label={t('common:password')}
                name="password"
                value={password}
                onChange={(e): void => {
                  setError(null);
                  setPassword(e.target.value);
                }}
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                autoFocus
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={(): void => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                  inputProps: { 'data-testid': 'new-device-password' },
                }}
                fullWidth
                variant="filled"
                disabled={isLoading}
              />
            </Box>
            <Box mb={2}>
              <Button
                fullWidth
                variant="flat"
                type="submit"
                disabled={password.length < 6 || isLoading}
                size="large"
                data-testid="submit"
              >
                {t('common:pages.device.authenticate_device.button')}
              </Button>
            </Box>
            <ErrorText error={error} />
          </form>
        </Box>
      ) : (
        <StatusMessageBox type="success" message={t('common:device_added')} />
      )}
    </Modal>
  );
};

export default GainFullDeviceAccessModal;
